/**
 * CerPortalPayloadV1 — the versioned, QA-approved package for one CER portal
 * upload. Assembled by POST /api/admin/submissions/:jobId/:submittedAt/
 * approve-upload and served to the extension via
 * GET /api/job/:jobId/portal-payload.
 *
 * Everything upstream (intake, field capture, QA) targets this shape so the
 * upload TRANSPORT can be swapped — extension today, direct CER API if/when
 * granted (docs/cer-api-request.md) — without touching capture or QA.
 * Version bumps are explicit: v2 gets its own schema + a union at the
 * endpoint, never silent field drift.
 */
import { z } from 'zod';
import { ItpBatteryEntrySchema, AccreditationCodeSchema, PvAcIsolatorEntrySchema, PvDcIsolatorEntrySchema, PvInverterEntrySchema, PvModuleEntrySchema, } from './cer-portal.js';
/**
 * HARD RULE (ROADMAP decision #4): the portal's final "Initial findings
 * assessment" field is NEVER auto-selected — selecting it alerts CER that a
 * report is generated. Carried in-band on every payload so the extension
 * enforces it mechanically, not as tribal knowledge.
 */
export const NEVER_FILL_FIELDS = ['initial-findings-assessment'];
export const NeverFillFieldSchema = z.literal('initial-findings-assessment');
/**
 * Cover-sheet block, aligned to the CER portal's editable inputs. Dates are
 * YYYY-MM-DD, times HH:MM (customer-local — see InspectionCoverSheetSchema
 * notes on naive timestamps).
 */
export const CerPayloadCoversheetSchema = z.object({
    /** True when the homeowner signed the consent form themselves. */
    consentProviderIsOwner: z.boolean(),
    /** Set when the consent provider is NOT the owner (tenant / agent / partner). */
    consentProviderFirstName: z.string().optional(),
    consentProviderSurname: z.string().optional(),
    inspectionStartDate: z.string(),
    inspectionStartTime: z.string(),
    inspectionEndDate: z.string(),
    inspectionEndTime: z.string(),
    /** e.g. "009CER-319-B". */
    inspectorIdCardNumber: z.string(),
    /**
     * Inspector attestation of no conflict of interest. Not captured as a
     * field-PWA input yet — assembly defaults it to true (the roster never
     * inspects their own installs); M3 adds explicit capture.
     */
    noConflictOfInterest: z.boolean(),
    roofCavityInspected: z.boolean(),
    /** Portal delivery method. Current workflow always selects `email`. */
    reportReceivedBy: z.string(),
    /** Fills the portal's "Updated Owner Email Address" when the feed's email was wrong. */
    inspectorRecommendation: z.string().optional(),
    updatedOwnerEmail: z.string().optional(),
    updatedConsentProviderEmail: z.string().optional(),
});
/** Reference into the field-store photo manifests (no blob bytes in the payload). */
export const CerPayloadPhotoRefSchema = z.object({
    photoId: z.string(),
    /** Photo-prompt key (or `clause:<checklistItemId>` for evidence shots). */
    promptKey: z.string(),
    filename: z.string(),
    capturedAt: z.string().optional(),
    /** S3 key / local-fs path — lets the uploader fetch bytes via the field store. */
    storageKey: z.string().optional(),
});
/** One portal checklist answer, with the QA-approved assessment write-up. */
export const CerPayloadChecklistAnswerSchema = z.object({
    /** e.g. "SB & Labelling 13" — matches the assessment library. */
    checklistItemId: z.string(),
    /** Stable portal-compatible Initial Findings Assessment value. */
    assessment: z.string().optional(),
    /** Field outcome, verbatim ('Compliant' | 'Non-Compliant' | 'Not Applicable'). */
    answer: z.string(),
    severity: z.string().optional(),
    /** Assessment paragraphs (library wording or inspector-edited), in fill order. */
    assessmentWording: z.array(z.string()).optional(),
    /**
     * Evidence shots captured against this clause — the field PWA's
     * `clause:<checklistItemId>` photos, resolved to refs.
     *
     * These belong here rather than in `hardware.photos` because the portal
     * renders a separate upload input on every clause row
     * (`SubmissionCIResponseListGroup[g].SubmissionCIResponseList[i].UploadedChecklistFiles`),
     * and the only thing that says which row a photo belongs on is the clause it
     * was captured against. Flattening them into one list would throw that away
     * and leave the uploader re-deriving it from the `clause:` prefix.
     *
     * Empty for clauses with no evidence, which is most of them.
     */
    photos: z.array(CerPayloadPhotoRefSchema).default([]),
});
export const CerPortalPayloadV1Schema = z.object({
    version: z.literal(1),
    /** Accreditation code / job id, e.g. "BTY6303119". */
    caseId: AccreditationCodeSchema,
    relatedAccreditationCode: AccreditationCodeSchema.optional(),
    /** Which portal login uploads this (e.g. "Jason Satchell"). */
    portalAccount: z.string().optional(),
    coversheet: CerPayloadCoversheetSchema,
    /** Photo manifest refs for the hardware evidence uploads. */
    hardware: z.object({
        totalArrayVoltageOpenCircuitV: z.number().nonnegative().optional(),
        totalArrayCurrentShortCircuitA: z.number().nonnegative().optional(),
        photos: z.array(CerPayloadPhotoRefSchema),
        inverters: z.array(PvInverterEntrySchema).default([]),
        modules: z.array(PvModuleEntrySchema).default([]),
        acIsolators: z.array(PvAcIsolatorEntrySchema).default([]),
        dcIsolators: z.array(PvDcIsolatorEntrySchema).default([]),
        batteries: z.array(ItpBatteryEntrySchema).default([]),
        /** → `Circuit_Breaker_Size_Main_Switch_Grid`. Digits, at most 2 decimal places. */
        circuitBreakerSizeMainSwitchGrid: z.string().optional(),
        /** → `Circuit_Breaker_Size_Main_Switch_Inverter_Alternative`. */
        circuitBreakerSizeMainSwitchInverter: z.string().optional(),
        /** → `Alternative_Supply_From_BESS_Inverter_Flag`. */
        alternativeSupplyFromBessInverter: z.boolean().optional(),
        /** → `Switchboard_Labelling_Requirements_Met`. */
        switchboardLabellingRequirementsMet: z.boolean().optional(),
    }),
    /** General inspection/improvement evidence for the final Assessment page. */
    assessmentPhotos: z.array(CerPayloadPhotoRefSchema).default([]),
    nonChecklistResponses: z.record(z.string(), z.string()).optional(),
    checklistAnswers: z.array(CerPayloadChecklistAnswerSchema),
    /** Always present, always non-empty — see NEVER_FILL_FIELDS. */
    neverFill: z.array(NeverFillFieldSchema).min(1),
});
/** Authenticated extension response. Only QA-approved payloads use this wire shape. */
export const ApprovedPortalPayloadResponseSchema = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true), payload: CerPortalPayloadV1Schema }),
    z.object({
        ok: z.literal(false),
        error: z.object({
            code: z.enum(['UNAUTHORIZED', 'NOT_FOUND', 'INVALID_JOB_ID', 'INTERNAL']),
            message: z.string(),
        }),
    }),
]);
//# sourceMappingURL=cer-payload.js.map